import { Inject, Injectable, InjectionToken } from '@angular/core';

type StoredQuotaOperations = Record<string, string>;

export const QUOTA_OPERATION_STORAGE = new InjectionToken<Storage | null>('QUOTA_OPERATION_STORAGE', {
  providedIn: 'root',
  factory: (): Storage | null => {
    try {
      return typeof sessionStorage === 'undefined' ? null : sessionStorage;
    } catch {
      return null;
    }
  },
});

const STORAGE_KEY = 'idoc-ai-quota-operations-v1';

@Injectable({ providedIn: 'root' })
export class QuotaOperationIdService {
  private readonly fallbackOperations: Map<string, string> = new Map<string, string>();

  constructor(@Inject(QUOTA_OPERATION_STORAGE) private readonly storage: Storage | null) {}

  getOrCreate(scope: string, signature: string): string {
    const key = this.getKey(scope, signature);
    const operations = this.readOperations();
    const existing = operations[key] ?? this.fallbackOperations.get(key);
    if (existing) {
      return existing;
    }

    const operationId = crypto.randomUUID();
    operations[key] = operationId;
    this.fallbackOperations.set(key, operationId);
    this.writeOperations(operations);
    return operationId;
  }

  release(scope: string, signature: string, operationId: string): void {
    const key = this.getKey(scope, signature);
    const operations = this.readOperations();
    if (operations[key] === operationId) {
      delete operations[key];
      this.writeOperations(operations);
    }
    if (this.fallbackOperations.get(key) === operationId) {
      this.fallbackOperations.delete(key);
    }
  }

  clearAll(): void {
    this.fallbackOperations.clear();
    try {
      this.storage?.removeItem(STORAGE_KEY);
    } catch {
      // In-memory state is already clear; inaccessible browser storage can be ignored.
    }
  }

  private getKey(scope: string, signature: string): string {
    return `${scope}\u0000${signature}`;
  }

  private readOperations(): StoredQuotaOperations {
    if (!this.storage) {
      return Object.fromEntries(this.fallbackOperations);
    }

    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (!raw) {
        return {};
      }
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return {};
      }
      return Object.fromEntries(
        Object.entries(parsed).filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
      );
    } catch {
      return Object.fromEntries(this.fallbackOperations);
    }
  }

  private writeOperations(operations: StoredQuotaOperations): void {
    if (!this.storage) {
      return;
    }

    try {
      if (Object.keys(operations).length === 0) {
        this.storage.removeItem(STORAGE_KEY);
      } else {
        this.storage.setItem(STORAGE_KEY, JSON.stringify(operations));
      }
    } catch {
      // In-memory state still preserves retries while this application instance is alive.
    }
  }
}
